// src/components/UserMeet/layouts/StageLayout50.jsx
// Gallery view for 50-person Town Hall rooms
import React, { useMemo, useState } from "react";
import LiveKitVideo from "../../../pages/MeetingRoom/ui/LiveKitVideo";
import Icon from "../../../pages/MeetingRoom/ui/Icon";

const AVATAR_GRADIENTS = [
  ["#4ADE80", "#16A34A"],
  ["#38BDF8", "#0284C7"],
  ["#FCD34D", "#D97706"],
  ["#C084FC", "#7C3AED"],
  ["#FB7185", "#BE123C"],
  ["#FB923C", "#C2410C"],
  ["#34D399", "#059669"],
  ["#A78BFA", "#5B21B6"],
];

const getAvatarColors = (name = "") => {
  if (!name || typeof name !== "string") return AVATAR_GRADIENTS[0];
  return AVATAR_GRADIENTS[name.charCodeAt(0) % AVATAR_GRADIENTS.length];
};

function GalleryTile({ participant, isSpotlight = false, onPress }) {
  if (!participant) return null;

  const {
    name,
    image,
    isMuted,
    isSpeaking,
    isLocal,
    hasVideo,
    trackRef,
    isCameraOn,
  } = participant;
  const [c1, c2] = getAvatarColors(name);

  return (
    <div
      className={`jt-gallery-tile ${isSpeaking ? "speaking" : ""} ${isSpotlight ? "spotlight" : ""}`}
      onClick={() => onPress?.(participant)}
      role="button"
      tabIndex={0}
    >
      <div className="jt-gallery-media">
        {hasVideo && trackRef ? (
          <LiveKitVideo
            trackRef={trackRef}
            muted={isLocal}
            mirror={isLocal}
            objectFit="cover"
            className="jt-gallery-video"
          />
        ) : (
          <div
            className="jt-gallery-avatar"
            style={{
              background: `linear-gradient(135deg, ${c1}, ${c2})`,
            }}
          >
            {image ? (
              <img src={image} alt={name} className="jt-gallery-avatarImg" />
            ) : (
              <span className="jt-gallery-avatarText">
                {(name?.[0] || "?").toUpperCase()}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Bottom bar */}
      <div className="jt-gallery-bar">
        <div className={`jt-gallery-mic ${isMuted ? "off" : "on"}`}>
          {isMuted ? <Icon name="micOff" /> : <Icon name="mic" />}
        </div>
        <span className="jt-gallery-name">
          {isLocal ? "You" : name || "Unknown"}
        </span>
        {isCameraOn && hasVideo && <div className="jt-gallery-camDot" />}
      </div>

      {/* Speaking indicator */}
      {isSpeaking && <div className="jt-gallery-speakingBorder" />}
    </div>
  );
}

export default function StageLayout50({
  regularParticipants,
  activeSpeaker,
  screenShareTrack,
  onAvatarPress,
  onLike,
}) {
  const [page, setPage] = useState(0);

  // Person currently speaking gets spotlight
  const spotlightParticipant = activeSpeaker || regularParticipants[0] || null;

  // Everyone else in the gallery grid
  const galleryParticipants = useMemo(() => {
    if (!spotlightParticipant) return regularParticipants;
    return regularParticipants.filter((p) => p.id !== spotlightParticipant.id);
  }, [regularParticipants, spotlightParticipant]);

  const TILES_PER_PAGE = 15;
  const totalPages = Math.max(
    1,
    Math.ceil(galleryParticipants.length / TILES_PER_PAGE),
  );
  const currentPage = Math.min(page, totalPages - 1);

  const visibleParticipants = useMemo(() => {
    const start = currentPage * TILES_PER_PAGE;
    return galleryParticipants.slice(start, start + TILES_PER_PAGE);
  }, [galleryParticipants, currentPage]);

  if (screenShareTrack) return null;

  return (
    <div className="jt-gallery-root">
      {/* Spotlight — active speaker or first participant */}
      {spotlightParticipant && (
        <div className="jt-gallery-spotlight">
          <GalleryTile
            participant={spotlightParticipant}
            isSpotlight
            onPress={onAvatarPress}
          />
        </div>
      )}

      {/* Gallery grid */}
      <div className="jt-gallery-grid">
        {visibleParticipants.map((p) => (
          <GalleryTile key={p.id} participant={p} onPress={onAvatarPress} />
        ))}

        {/* Empty slots visual hint */}
        {visibleParticipants.length === 0 && (
          <div className="jt-gallery-empty">
            <Icon name="people" />
            <span>Waiting for participants…</span>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="jt-gallery-pagination">
          <button
            className="jt-gallery-pageBtn"
            disabled={currentPage === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            ‹
          </button>
          <span className="jt-gallery-pageInfo">
            {currentPage + 1} / {totalPages}
          </span>
          <button
            className="jt-gallery-pageBtn"
            disabled={currentPage >= totalPages - 1}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}
